// Board Generation Logic - Creates personalized 100-square journey

export const generatePersonalizedBoard = (userProfile) => {
  const {
    mainStruggle,
    primaryGoal,
    procrastinationLevel,
    selfWorthLevel,
    deservingLevel,
    moneyFeeling,
    consistencyIssue,
    spiritualBackground,
  } = userProfile;

  // Available squares library (categorized by theme)
  const squareLibrary = {
    // FOUNDATION PHASE (1-25) - Universal for everyone
    foundation: [
      {
        id: 1,
        title: "Morning Intention",
        desc: "Voice record: 'Today I practice being enough' (2 min)",
        type: "intention",
        priority: "high",
      },
      {
        id: 2,
        title: "Breath Awareness",
        desc: "Notice 10 breaths without judgment (2 min)",
        type: "practice",
        priority: "high",
      },
      {
        id: 3,
        title: "Self-Gratitude",
        desc: "Name 3 things you're grateful for about yourself (3 min)",
        type: "reflection",
        priority: "high",
      },
      {
        id: 4,
        title: "Body Check-In",
        desc: "Scan your body for tension and breathe into it (3 min)",
        type: "practice",
        priority: "medium",
      },
      {
        id: 5,
        title: "Addiction Awareness",
        desc: "Notice one trigger moment without judgment (2 min)",
        type: "awareness",
        priority: "high",
      },
      {
        id: 6,
        title: "Present Moment",
        desc: "Describe what you see, hear, feel right now (2 min)",
        type: "practice",
        priority: "medium",
      },
      {
        id: 7,
        title: "Small Task",
        desc: "Complete one small avoided task (5 min)",
        type: "action",
        priority: "high",
      },
      {
        id: 8,
        title: "Daily Win",
        desc: "Voice journal one thing you did well today (2 min)",
        type: "reflection",
        priority: "high",
      },
      {
        id: 9,
        title: "Self-Compassion",
        desc: "Say to yourself: 'I'm doing my best' (2 min)",
        type: "intention",
        priority: "medium",
      },
      {
        id: 10,
        title: "Fear Naming",
        desc: "Name one fear you're carrying today (2 min)",
        type: "reflection",
        priority: "medium",
      },
      {
        id: 11,
        title: "Vulnerable Connection",
        desc: "Reach out to one friend authentically (5 min)",
        type: "action",
        priority: "medium",
      },
      {
        id: 12,
        title: "Body Movement",
        desc: "Move your body for 5 minutes with joy (5 min)",
        type: "action",
        priority: "high",
      },
      {
        id: 13,
        title: "Imperfection Practice",
        desc: "Do something imperfectly on purpose (3 min)",
        type: "action",
        priority: "medium",
      },
      {
        id: 14,
        title: "Childhood Memory",
        desc: "Recall one happy childhood moment (3 min)",
        type: "reflection",
        priority: "low",
      },
      {
        id: 15,
        title: "Core Truth",
        desc: "Your worth exists before any outcome (meditate 5 min)",
        type: "wisdom",
        priority: "high",
      },
      {
        id: 16,
        title: "Anger Expression",
        desc: "Write out your frustrations uncensored (5 min)",
        type: "reflection",
        priority: "medium",
      },
      {
        id: 17,
        title: "Comfort Avoidance",
        desc: "What am I avoiding by staying comfortable? (3 min)",
        type: "reflection",
        priority: "high",
      },
      {
        id: 18,
        title: "Forgiveness Start",
        desc: "Write: 'I'm willing to forgive...' (3 min)",
        type: "reflection",
        priority: "medium",
      },
      {
        id: 19,
        title: "Joy Memory",
        desc: "Remember when you felt fully alive (3 min)",
        type: "reflection",
        priority: "low",
      },
      {
        id: 20,
        title: "Creative Joy",
        desc: "Do something creative for pure fun (5 min)",
        type: "action",
        priority: "medium",
      },
      {
        id: 21,
        title: "Shadow Work",
        desc: "Name one quality you judge in others (3 min)",
        type: "reflection",
        priority: "medium",
      },
      {
        id: 22,
        title: "Energy Boundaries",
        desc: "Notice who drains vs. fills your energy (3 min)",
        type: "awareness",
        priority: "medium",
      },
      {
        id: 23,
        title: "Vulnerability Share",
        desc: "Share one vulnerable truth with someone (3 min)",
        type: "action",
        priority: "high",
      },
      {
        id: 24,
        title: "Pain Acknowledgment",
        desc: "Say: 'This hurts and that's okay' (2 min)",
        type: "intention",
        priority: "medium",
      },
      {
        id: 25,
        title: "Foundation Review",
        desc: "What have I learned about myself? (5 min)",
        type: "reflection",
        priority: "high",
      },
    ],

    // MONEY WOUND SQUARES (for users with money issues)
    money: [
      {
        id: 26,
        title: "Money Origin",
        desc: "Write where you learned 'money = wrong' (5 min)",
        type: "reflection",
        priority: "high",
      },
      {
        id: 27,
        title: "Body Money Scan",
        desc: "Feel body's response to receiving money (5 min)",
        type: "practice",
        priority: "high",
      },
      {
        id: 28,
        title: "Scarcity Voice",
        desc: "Write what scarcity says to you (5 min)",
        type: "reflection",
        priority: "medium",
      },
      {
        id: 29,
        title: "Receiving Practice",
        desc: "Accept one compliment without deflecting (2 min)",
        type: "action",
        priority: "high",
      },
      {
        id: 30,
        title: "Spiritual Money",
        desc: "Spiritual people need money to do spiritual work (reflect 3 min)",
        type: "wisdom",
        priority: "high",
      },
      {
        id: 31,
        title: "Guilt Exploration",
        desc: "When do I feel guilty about money? (5 min)",
        type: "reflection",
        priority: "medium",
      },
      {
        id: 32,
        title: "Generosity Memory",
        desc: "Remember when you gave freely (3 min)",
        type: "reflection",
        priority: "low",
      },
      {
        id: 33,
        title: "Abundance Vision",
        desc: "If I deserved $3000/month, I would... (5 min)",
        type: "reflection",
        priority: "high",
      },
      {
        id: 34,
        title: "Your Worth",
        desc: "Say out loud: 'My rate is $250' 10 times (2 min)",
        type: "action",
        priority: "high",
      },
      {
        id: 35,
        title: "Money Affirmation",
        desc: "Write: 'I receive with gratitude' (2 min)",
        type: "intention",
        priority: "medium",
      },
      {
        id: 36,
        title: "Money Math",
        desc: "Calculate: 12 sessions x $250 = $3000 (2 min)",
        type: "action",
        priority: "high",
      },
      {
        id: 37,
        title: "Receiving Visualization",
        desc: "See yourself receiving $1000 joyfully (5 min)",
        type: "practice",
        priority: "medium",
      },
      {
        id: 38,
        title: "Value Given",
        desc: "Write ways I've given value to others (5 min)",
        type: "reflection",
        priority: "high",
      },
      {
        id: 39,
        title: "I Deserve",
        desc: "Voice record: 'I deserve to receive' (2 min)",
        type: "intention",
        priority: "high",
      },
      {
        id: 40,
        title: "Money Story Reframe",
        desc: "My new relationship with money is... (5 min)",
        type: "reflection",
        priority: "high",
      },
    ],

    // SHAME/WORTH SQUARES (for users with self-worth issues)
    shame: [
      {
        id: 41,
        title: "Shame Voice Dialogue",
        desc: "What does shame say to you? (5 min)",
        type: "reflection",
        priority: "high",
      },
      {
        id: 42,
        title: "Enough Practice",
        desc: "Say: 'I am enough' 20 times (3 min)",
        type: "action",
        priority: "high",
      },
      {
        id: 43,
        title: "Comparison Trap",
        desc: "Who do I compare myself to and why? (5 min)",
        type: "reflection",
        priority: "medium",
      },
      {
        id: 44,
        title: "Enoughness Meditation",
        desc: "Sit with 'I am already whole' (5 min)",
        type: "practice",
        priority: "high",
      },
      {
        id: 45,
        title: "Achievement Detachment",
        desc: "My worth exists before accomplishment (3 min)",
        type: "wisdom",
        priority: "high",
      },
      {
        id: 46,
        title: "Inner Child Healing",
        desc: "What did young you need to hear? (5 min)",
        type: "reflection",
        priority: "medium",
      },
      {
        id: 47,
        title: "Perfection Release",
        desc: "I release the need to be perfect (3 min)",
        type: "intention",
        priority: "high",
      },
      {
        id: 48,
        title: "Self-Love Letter",
        desc: "Write a love letter to yourself (10 min)",
        type: "reflection",
        priority: "medium",
      },
      {
        id: 49,
        title: "Shame Sharing",
        desc: "Share one shame with a trusted person (5 min)",
        type: "action",
        priority: "high",
      },
      {
        id: 50,
        title: "Worthiness Embodiment",
        desc: "Walk as if you're worthy (5 min)",
        type: "practice",
        priority: "medium",
      },
    ],

    // CONSISTENCY/PROCRASTINATION SQUARES
    consistency: [
      {
        id: 51,
        title: "Build Something",
        desc: "Work on project for 10 minutes (10 min)",
        type: "action",
        priority: "high",
      },
      {
        id: 52,
        title: "Share Work",
        desc: "Post about your work on ONE platform (5 min)",
        type: "action",
        priority: "high",
      },
      {
        id: 53,
        title: "Procrastination Pattern",
        desc: "When do I avoid? What triggers it? (5 min)",
        type: "reflection",
        priority: "high",
      },
      {
        id: 54,
        title: "One Small Step",
        desc: "Take one tiny action toward goal (5 min)",
        type: "action",
        priority: "high",
      },
      {
        id: 55,
        title: "Completion Joy",
        desc: "Remember how finishing something felt (3 min)",
        type: "reflection",
        priority: "medium",
      },
      {
        id: 56,
        title: "Purpose Reconnect",
        desc: "Who am I helping with this work? (3 min)",
        type: "reflection",
        priority: "high",
      },
      {
        id: 57,
        title: "Imperfect Creation",
        desc: "Complete one thing imperfectly (5 min)",
        type: "action",
        priority: "high",
      },
      {
        id: 58,
        title: "Public Progress",
        desc: "Share progress vulnerably (3 min)",
        type: "action",
        priority: "medium",
      },
      {
        id: 59,
        title: "Fear of Success",
        desc: "What scares me about succeeding? (5 min)",
        type: "reflection",
        priority: "medium",
      },
      {
        id: 60,
        title: "Consistency Wisdom",
        desc: "Consistency is showing up, not being perfect (3 min)",
        type: "wisdom",
        priority: "high",
      },
      {
        id: 61,
        title: "Discipline vs Love",
        desc: "Can I do this from love, not force? (3 min)",
        type: "reflection",
        priority: "medium",
      },
      {
        id: 62,
        title: "Addiction Reduction",
        desc: "Reduce phone/weed/porn by 20% today (track)",
        type: "practice",
        priority: "high",
      },
      {
        id: 63,
        title: "Morning Routine",
        desc: "Create 15-min morning ritual (5 min plan)",
        type: "action",
        priority: "medium",
      },
      {
        id: 64,
        title: "Progress Journal",
        desc: "Voice journal progress I've made (3 min)",
        type: "reflection",
        priority: "high",
      },
      {
        id: 65,
        title: "Accountability Partner",
        desc: "Ask one person to hold you accountable (5 min)",
        type: "action",
        priority: "medium",
      },
    ],

    // RELATIONSHIP/BOUNDARY SQUARES
    relationships: [
      {
        id: 66,
        title: "Boundary Audit",
        desc: "Where do I say yes but mean no? (5 min)",
        type: "reflection",
        priority: "high",
      },
      {
        id: 67,
        title: "No Practice",
        desc: "Say no to one request today (varies)",
        type: "action",
        priority: "high",
      },
      {
        id: 68,
        title: "People-Pleasing Origin",
        desc: "When did I learn to please others? (5 min)",
        type: "reflection",
        priority: "medium",
      },
      {
        id: 69,
        title: "Energy Vampires",
        desc: "Who drains me and why? (5 min)",
        type: "reflection",
        priority: "medium",
      },
      {
        id: 70,
        title: "Authentic Expression",
        desc: "Say one truth you've been hiding (3 min)",
        type: "action",
        priority: "high",
      },
      {
        id: 71,
        title: "Codependency Check",
        desc: "Am I trying to fix/save someone? (3 min)",
        type: "reflection",
        priority: "medium",
      },
      {
        id: 72,
        title: "Healthy Distance",
        desc: "Create space from one draining person (varies)",
        type: "action",
        priority: "high",
      },
      {
        id: 73,
        title: "Needs Identification",
        desc: "What do I actually need in relationships? (5 min)",
        type: "reflection",
        priority: "high",
      },
      {
        id: 74,
        title: "Communication Practice",
        desc: "Use 'I feel' statements today (3 min)",
        type: "action",
        priority: "medium",
      },
      {
        id: 75,
        title: "Self-Love Boundaries",
        desc: "Loving myself means saying no to... (5 min)",
        type: "reflection",
        priority: "high",
      },
    ],

    // SERVICE/PURPOSE SQUARES
    service: [
      {
        id: 76,
        title: "Free Session Offer",
        desc: "Offer one free energy healing session (5 min plan)",
        type: "action",
        priority: "high",
      },
      {
        id: 77,
        title: "Purpose Clarity",
        desc: "What breaks my heart about the world? (5 min)",
        type: "reflection",
        priority: "medium",
      },
      {
        id: 78,
        title: "Helping Reflection",
        desc: "How did it feel to help someone? (3 min)",
        type: "reflection",
        priority: "high",
      },
      {
        id: 79,
        title: "Service Vision",
        desc: "What would I do if I could help anyone? (5 min)",
        type: "reflection",
        priority: "medium",
      },
      {
        id: 80,
        title: "Set Your Rate",
        desc: "Decide: $250 or sliding $150-250 (2 min)",
        type: "action",
        priority: "high",
      },
      {
        id: 81,
        title: "Dharma Meditation",
        desc: "What is my unique gift to give? (5 min)",
        type: "practice",
        priority: "medium",
      },
      {
        id: 82,
        title: "Public Offering",
        desc: "Share: 'I'm accepting clients' (3 min)",
        type: "action",
        priority: "high",
      },
      {
        id: 83,
        title: "Impact Journal",
        desc: "How have I helped others this week? (5 min)",
        type: "reflection",
        priority: "medium",
      },
      {
        id: 84,
        title: "Service Boundaries",
        desc: "I can serve AND protect my energy (3 min)",
        type: "wisdom",
        priority: "high",
      },
      {
        id: 85,
        title: "Book First Client",
        desc: "Schedule first PAID session (5 min)",
        type: "milestone",
        priority: "high",
      },
      {
        id: 86,
        title: "Pre-Session",
        desc: "Before session: 'I deserve to receive' (2 min)",
        type: "intention",
        priority: "high",
      },
      {
        id: 87,
        title: "Paid Session",
        desc: "DO the paid healing session (60 min)",
        type: "milestone",
        priority: "high",
      },
      {
        id: 88,
        title: "Receive Payment",
        desc: "Notice body sensations while receiving (2 min)",
        type: "practice",
        priority: "high",
      },
      {
        id: 89,
        title: "Post-Session Reflection",
        desc: "How did that feel in my body? (5 min)",
        type: "reflection",
        priority: "high",
      },
      {
        id: 90,
        title: "Client Gratitude",
        desc: "Send thank you to client (3 min)",
        type: "action",
        priority: "medium",
      },
      {
        id: 91,
        title: "Receiving Reflection",
        desc: "How did receiving money feel? (5 min)",
        type: "reflection",
        priority: "high",
      },
      {
        id: 92,
        title: "Rate Adjustment",
        desc: "Am I charging enough? Adjust if needed (3 min)",
        type: "action",
        priority: "medium",
      },
      {
        id: 93,
        title: "Book Second",
        desc: "Book 2nd paid session (momentum!) (3 min)",
        type: "action",
        priority: "high",
      },
      {
        id: 94,
        title: "Celebration",
        desc: "Celebrate: You're a professional healer! (5 min)",
        type: "milestone",
        priority: "high",
      },
      {
        id: 95,
        title: "Divine Service",
        desc: "I serve divinely AND receive abundance (meditate 5 min)",
        type: "wisdom",
        priority: "high",
      },
      {
        id: 96,
        title: "Abundance Vision",
        desc: "Envision: 12 clients/month = $3000 (3 min)",
        type: "reflection",
        priority: "high",
      },
      {
        id: 97,
        title: "Scaling Strategy",
        desc: "How can I help more people? (5 min)",
        type: "reflection",
        priority: "medium",
      },
      {
        id: 98,
        title: "Teaching Others",
        desc: "Share one thing you learned (5 min)",
        type: "action",
        priority: "medium",
      },
      {
        id: 99,
        title: "Legacy Reflection",
        desc: "What mark do I want to leave? (5 min)",
        type: "reflection",
        priority: "low",
      },
      {
        id: 100,
        title: "MOKSHA",
        desc: "Liberation: You are enough, you finish, you receive. (10 min)",
        type: "milestone",
        priority: "high",
      },
    ],
  };

  // STEP 1: Determine user's primary wounds/goals
  const userNeeds = analyzeUserNeeds(userProfile);

  // STEP 2: Select relevant squares for phases 2-4
  const selectedSquares = selectRelevantSquares(squareLibrary, userNeeds);

  // STEP 3: Arrange squares in optimal order
  const orderedBoard = arrangeSquares(selectedSquares, userNeeds);

  // STEP 4: Place snakes and ladders
  const boardWithMechanics = placeSnakesAndLadders(orderedBoard, userProfile);

  // STEP 5: Add phase metadata
  const finalBoard = addPhaseMetadata(boardWithMechanics);

  return finalBoard;
};

// Analyze user's primary needs from assessment
const analyzeUserNeeds = (profile) => {
  const needs = {
    money: 0,
    shame: 0,
    consistency: 0,
    relationships: 0,
    service: 0,
  };

  // Analyze main struggles
  if (profile.mainStruggle) {
    profile.mainStruggle.forEach((struggle) => {
      if (struggle === "money") needs.money += 3;
      if (struggle === "shame" || struggle === "anxiety") needs.shame += 3;
      if (struggle === "procrastination") needs.consistency += 3;
      if (struggle === "boundaries" || struggle === "heartbreak")
        needs.relationships += 3;
      if (struggle === "purpose") needs.service += 2;
    });
  }

  // Analyze primary goals
  if (profile.primaryGoal) {
    profile.primaryGoal.forEach((goal) => {
      if (goal === "money") needs.money += 2;
      if (goal === "shame") needs.shame += 2;
      if (goal === "consistency") needs.consistency += 2;
      if (goal === "relationships") needs.relationships += 2;
      if (goal === "service" || goal === "purpose") needs.service += 2;
    });
  }

  // Analyze scale responses
  if (profile.deservingLevel && profile.deservingLevel < 5) needs.money += 2;
  if (profile.selfWorthLevel && profile.selfWorthLevel < 5) needs.shame += 2;
  if (profile.procrastinationLevel && profile.procrastinationLevel > 6)
    needs.consistency += 2;

  return needs;
};

// Select relevant squares based on user needs
const selectRelevantSquares = (library, needs) => {
  const selected = {
    foundation: [...library.foundation], // Everyone gets foundation
    phase2: [],
    phase3: [],
    phase4: [],
  };

  // Sort needs by priority
  const sortedNeeds = Object.entries(needs).sort((a, b) => b[1] - a[1]);

  // Primary need (highest score) â†’ Phase 2 (squares 26-50)
  const primaryNeed = sortedNeeds[0][0];
  selected.phase2 = selectSquaresFromCategory(library[primaryNeed], 15, "high");

  // Secondary need â†’ Phase 3 (squares 51-75)
  const secondaryNeed = sortedNeeds[1][0];
  selected.phase3 = selectSquaresFromCategory(
    library[secondaryNeed],
    15,
    "high"
  );

  // Everyone gets service phase at the end
  selected.phase4 = [...library.service];

  return selected;
};

// Select squares from a category based on priority
const selectSquaresFromCategory = (squares, count, priorityLevel) => {
  // Filter by priority
  const prioritized = squares.filter((s) => s.priority === priorityLevel);

  // If not enough high priority, add medium priority
  if (prioritized.length < count) {
    const medium = squares.filter((s) => s.priority === "medium");
    prioritized.push(...medium.slice(0, count - prioritized.length));
  }

  // Shuffle and take the count needed
  return shuffleArray(prioritized).slice(0, count);
};

// Arrange squares in optimal progression
const arrangeSquares = (selectedSquares, userNeeds) => {
  const board = [];

  // Phase 1: Foundation (1-25) - Keep as is
  selectedSquares.foundation.forEach((square, index) => {
    board.push({
      ...square,
      position: index + 1,
      phase: "foundation",
    });
  });

  // Phase 2: Primary wound (26-50)
  selectedSquares.phase2.forEach((square, index) => {
    board.push({
      ...square,
      position: 26 + index,
      phase: "primary",
    });
  });

  // Fill remaining phase 2 slots if needed
  let primaryCount = board.filter((s) => s.phase === "primary").length;
  while (primaryCount < 25) {
    board.push({
      id: 26 + primaryCount,
      title: "Reflection Day",
      desc: "Journal about your progress (5 min)",
      type: "reflection",
      position: 26 + primaryCount,
      phase: "primary",
      priority: "low",
    });
    primaryCount++;
  }

  // Phase 3: Secondary wound (51-75)
  selectedSquares.phase3.forEach((square, index) => {
    board.push({
      ...square,
      position: 51 + index,
      phase: "secondary",
    });
  });

  // Fill remaining phase 3 slots
  let secondaryCount = board.filter((s) => s.phase === "secondary").length;
  while (secondaryCount < 25) {
    board.push({
      id: 51 + secondaryCount,
      title: "Integration Day",
      desc: "Reflect on what you've learned (5 min)",
      type: "reflection",
      position: 51 + secondaryCount,
      phase: "secondary",
      priority: "low",
    });
    secondaryCount++;
  }

  // Phase 4: Service (76-100) - Everyone gets this
  selectedSquares.phase4.forEach((square, index) => {
    board.push({
      ...square,
      position: 76 + index,
      phase: "service",
    });
  });

  return board;
};

// Place snakes (setbacks) and ladders (breakthroughs)
const placeSnakesAndLadders = (board, profile) => {
  const mechanics = [];

  // ── PERMANENT SNAKES — one per phase, always present ──────────────────────
  mechanics.push({
    type: "snake", fromSquare: 17, toSquare: 7,
    reason: "Pride Before the Fall",
    description: "The ego crept in — back to humility and foundation",
  });
  mechanics.push({
    type: "snake", fromSquare: 46, toSquare: 36,
    reason: "Old Story Returns",
    description: "The wound spoke louder than the healing — revisit the work",
  });
  mechanics.push({
    type: "snake", fromSquare: 62, toSquare: 52,
    reason: "Distraction Loop",
    description: "Pulled away from practice — return to consistent action",
  });
  mechanics.push({
    type: "snake", fromSquare: 85, toSquare: 74,
    reason: "Fear of Being Seen",
    description: "Shrinking before the final stretch — back to boundaries work",
  });
  mechanics.push({
    type: "snake", fromSquare: 97, toSquare: 88,
    reason: "Final Resistance",
    description: "Liberation is close — the ego fights hardest at the end",
  });

  // ── PERMANENT LADDERS — one per phase, always present ─────────────────────
  mechanics.push({
    type: "ladder", fromSquare: 10, toSquare: 20,
    reason: "Self-Awareness Breakthrough",
    description: "You saw the pattern clearly — leap forward in understanding",
  });
  mechanics.push({
    type: "ladder", fromSquare: 34, toSquare: 44,
    reason: "Compassion Opens",
    description: "You stopped fighting yourself — the heart opens, advance",
  });
  mechanics.push({
    type: "ladder", fromSquare: 57, toSquare: 67,
    reason: "Momentum Mastered",
    description: "Consistency clicked — celebrate and leap forward",
  });
  mechanics.push({
    type: "ladder", fromSquare: 76, toSquare: 86,
    reason: "Purpose Ignited",
    description: "Your why became undeniable — leap into service",
  });
  mechanics.push({
    type: "ladder", fromSquare: 90, toSquare: 95,
    reason: "Abundance Received",
    description: "You proved you can receive — almost at the summit",
  });

  // ── CONDITIONAL EXTRAS — based on user profile ────────────────────────────
  if (profile.selfWorthLevel < 6) {
    mechanics.push({
      type: "snake", fromSquare: 9, toSquare: 4,
      reason: "Self-Judgment",
      description: "Caught in harsh self-criticism — return to compassion practice",
    });
  }
  if (profile.mainStruggle?.includes("money")) {
    mechanics.push({
      type: "snake", fromSquare: 31, toSquare: 27,
      reason: "Money Shame",
      description: "Body rejecting receiving — return to body scan",
    });
    mechanics.push({
      type: "ladder", fromSquare: 40, toSquare: 45,
      reason: "Money Wound Healed",
      description: "Understanding money as energy — leap forward",
    });
  }
  if (profile.procrastinationLevel > 6) {
    mechanics.push({
      type: "ladder", fromSquare: 23, toSquare: 30,
      reason: "Imperfect Action Taken",
      description: "You did it even when scared — advance",
    });
  }

  return { board, mechanics };
};

// Add phase metadata
const addPhaseMetadata = (boardData) => {
  const phases = {
    foundation: {
      name: "Foundation",
      icon: "ðŸŒ…",
      description: "Building self-awareness and compassion",
      range: [1, 25],
    },
    primary: {
      name: "Primary Healing",
      icon: "ðŸ’°", // Will be dynamic based on user
      description: "Addressing your deepest wound",
      range: [26, 50],
    },
    secondary: {
      name: "Integration",
      icon: "ðŸŽ¯",
      description: "Building new patterns",
      range: [51, 75],
    },
    service: {
      name: "Service & Abundance",
      icon: "ðŸ™",
      description: "Stepping into your gifts",
      range: [76, 100],
    },
  };

  return {
    squares: boardData.board,  // Rename board → squares
    snakes_and_ladders: boardData.mechanics || [],  // Rename mechanics → snakes_and_ladders
    phases,
  };
};

// Utility: Shuffle array
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};
